/**
 * The pure parts of the chat column's streaming behaviour (planning-app.md §3.6 *AI Assist* → *The
 * panel*, §13 `v3.32`): where the in-progress text splits into settled blocks and a live tail, how
 * the reveal is paced between the worker's flushes, when the column follows the text and when it
 * leaves the reader alone, and the keyboard inset a phone's visual viewport reports. No DOM here;
 * `useScrollFollow` and `StreamingMarkdown` apply these to the column.
 */

export type ScrollMetrics = { scrollTop: number; scrollHeight: number; clientHeight: number };

/** How close to the end (px) still counts as "at the bottom" — a line or two of body text. */
export const FOLLOW_THRESHOLD = 48;

/** The reader is at (or within `threshold` of) the end of the scroller. */
export function isNearBottom(m: ScrollMetrics, threshold = FOLLOW_THRESHOLD): boolean {
  return m.scrollHeight - m.clientHeight - m.scrollTop <= threshold;
}

/**
 * The follow state of a streaming column: it follows the text while the reader stays at the end, stops the
 * moment they scroll up, and resumes when they come back to the end or press *Jump to latest*. The column's
 * own scrolls are reported through `programmatic()` so they do not read as the reader's.
 */
export type ScrollFollow = {
  readonly following: boolean;
  /** A scroll event on the scroller; returns the follow state after it. */
  onScroll(m: ScrollMetrics): boolean;
  /**
   * Before the column moves the scroller itself: where it is now. A position above the last known one is the reader's
   * scroll whose event has not fired yet (they scrolled, the text grew, both in one frame) — moving the scroller
   * now would snap them back, so it is taken as their scroll first. Returns the follow state after it.
   */
  sync(m: ScrollMetrics): boolean;
  /** Called right before the column moves the scroller itself, to `top`: the scroll event that lands there is not the reader's. */
  programmatic(top: number): void;
  /** *Jump to latest*, or a new turn: follow again. */
  resume(): void;
  /** A new answer begins: the column follows it. */
  reset(): void;
};

/** A programmatic scroll's event arrives within a frame or two; one that never comes (nothing moved) must not swallow the reader's. */
export const PROGRAMMATIC_TTL_MS = 250;

export function createScrollFollow(initial = true, threshold = FOLLOW_THRESHOLD, now: () => number = () => Date.now()): ScrollFollow {
  let following = initial;
  let expected = Number.NaN;
  let expectedUntil = 0;
  let lastTop = Number.NaN;
  const isOurs = (top: number) => !Number.isNaN(expected) && now() <= expectedUntil && Math.abs(top - expected) <= 1.5;
  const onScroll = (m: ScrollMetrics) => {
    const wasTop = lastTop;
    lastTop = m.scrollTop;
    // The column's own scroll is known by where it lands (several sets in one frame fire one event, at the last
    // position); an event anywhere else is the reader's, even while one of ours is still expected.
    if (isOurs(m.scrollTop)) {
      expected = Number.NaN;
      return following;
    }
    if (now() > expectedUntil) expected = Number.NaN;
    // Only a move up can stop the follow: a move down is the reader going the text's way, or the browser's scroll anchoring
    // compensating for a block that settled above the live tail — neither is a wish to stop reading at the end.
    following = following ? m.scrollTop >= wasTop - 1 || isNearBottom(m, threshold) : isNearBottom(m, threshold);
    return following;
  };
  return {
    get following() {
      return following;
    },
    onScroll,
    sync(m) {
      if (Number.isNaN(lastTop) || m.scrollTop >= lastTop - 1 || isOurs(m.scrollTop)) return following;
      return onScroll(m);
    },
    programmatic(top) {
      expected = top;
      expectedUntil = now() + PROGRAMMATIC_TTL_MS;
    },
    resume() {
      following = true;
    },
    reset() {
      following = true;
      expected = Number.NaN;
    },
  };
}

/**
 * Splits a streaming answer into the blocks that will not change any more and the paragraph still being
 * written: everything up to the last blank line is settled, unless a code fence is open there — then the
 * fence and what follows it are the tail, so a half-written block is never parsed as finished Markdown.
 */
export function splitSettled(text: string): { settled: string; tail: string } {
  const cut = text.lastIndexOf('\n\n');
  if (cut < 0) return { settled: '', tail: text };
  const settled = text.slice(0, cut);
  let openFenceAt = -1;
  let offset = 0;
  for (const line of settled.split('\n')) {
    if (/^```/.test(line)) openFenceAt = openFenceAt < 0 ? offset : -1;
    offset += line.length + 1;
  }
  if (openFenceAt >= 0) return { settled: settled.slice(0, openFenceAt).replace(/\n+$/, ''), tail: text.slice(openFenceAt) };
  return { settled, tail: text.slice(cut + 2) };
}

/** The pace at which a flush's text is revealed: a flush's worth of text takes about one flush interval (ms) to appear. */
export const CATCH_UP_MS = 160;
/** What one of the worker's 150 ms flushes carries at a typical token rate (measured 2026-10-31: 15–50 characters). */
export const FLUSH_CHARS = 48;

/**
 * How many characters are shown after `dtMs` more of animation: a flush's worth of text (`FLUSH_CHARS`)
 * is revealed evenly over `catchUpMs`, so the reader sees a steady flow rather than the worker's lumps;
 * a larger gap (a reconnect, a late first flush of a whole answer) is closed faster, in proportion, so
 * it lands within a few intervals. At least one character per frame while there is a gap, never past
 * the target; a shorter target (a replaced message) snaps to it.
 */
export function paceReveal(shown: number, target: number, dtMs: number, catchUpMs = CATCH_UP_MS): number {
  if (target <= shown) return target;
  const gap = target - shown;
  const step = Math.max(1, Math.ceil((Math.max(gap, FLUSH_CHARS) * Math.max(0, dtMs)) / catchUpMs));
  return Math.min(target, shown + step);
}

/** A `[[…]]` marker, finished or not, at the end of a streaming tail: hidden until the final pass draws the chip. */
const TAIL_MARKER = /\[\[[^\]]*(?:\]\]|$)/g;

/** The tail's text with its citation markers taken out, so they neither flash as raw text nor turn into chips mid-stream. */
export function stripMarkers(tail: string): string {
  return tail.replace(TAIL_MARKER, '');
}

/**
 * The on-screen keyboard's height on a phone, from the visual viewport (what the browser does not
 * take out of the layout viewport); zero when nothing covers the page.
 */
export function keyboardInset(innerHeight: number, viewport: { height: number; offsetTop: number } | null | undefined): number {
  if (!viewport) return 0;
  return Math.max(0, Math.round(innerHeight - viewport.height - viewport.offsetTop));
}

/**
 * The end of the text (its offset in the scroller, above the spacer) is below what the reader sees: text is arriving
 * out of view, and *Jump to latest* has something to jump to. Not when the end is on screen — a reader who scrolled
 * up a little still sees the newest line, and a floating pill would only cover what they are reading.
 */
export function isEndBelowView(m: ScrollMetrics, endOffset: number, threshold = 24): boolean {
  return endOffset - (m.scrollTop + m.clientHeight) > threshold;
}

/** The bottom spacer under the last message: half the scroller's viewport, so new text lands mid-screen (the Cursor pane's rule). */
export function tailSpace(clientHeight: number): number {
  return Math.max(0, Math.round(clientHeight / 2));
}

/**
 * How tall the spacer needs to be so the scroller can put the end of the text at mid-view, and no taller: in the
 * panel — nothing in the scroller after the spacer — that is half the viewport; on a page whose composer sits in the
 * flow after it, that room counts, and a thread shorter than half a viewport needs none (a page that scrolls for
 * nothing only slides its sticky composer over the last message's controls).
 *
 * `end` is the text's end (the spacer's top) in the scroller's content, `current` the spacer as it is, `currentMax`
 * how far the scroller scrolls with it (`scrollHeight − clientHeight`). What follows the spacer is read off the
 * scroll range — exact once the content overflows; while it does not (a scroller shorter than its viewport reports
 * no range, whatever the slack) the spacer goes straight to the half, which makes the content overflow, and the next
 * measurement is exact. Two passes at most, and a pixel either way is left alone: the measurement settles, it does
 * not hunt (a spacer that crept towards its size by a small deficit a pass once ran into React's update limit).
 */
export function tailSpacer(clientHeight: number, end: number, current: number, currentMax: number): number {
  const half = tailSpace(clientHeight);
  if (end - half <= 0) return 0;
  if (currentMax <= 0) return half;
  const after = Math.max(0, currentMax + clientHeight - end - current);
  const next = Math.max(0, Math.min(half, Math.round(clientHeight - half - after)));
  return Math.abs(next - current) <= 1 ? current : next;
}
